import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const InvitationCard = ({
  invitation,
  status,
  isSelected,
  onSelect,
  onResend,
  isResending,
  timeSince,
  showCheckbox
}) => {
  const getStatusIcon = () => {
    if (status === 'accepted') return 'CheckCircle';
    if (status === 'declined') return 'XCircle';
    return 'Clock';
  };

  const getStatusColor = () => {
    if (status === 'accepted') return 'text-success';
    if (status === 'declined') return 'text-error';
    return 'text-warning';
  };

  const getStatusBgColor = () => {
    if (status === 'accepted') return 'bg-success/10';
    if (status === 'declined') return 'bg-error/10';
    return 'bg-warning/10';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`bg-background border rounded-lg p-4 transition-smooth hover-lift ${
      isSelected ? 'border-primary shadow-md' : 'border-border'
    }`}>
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        {showCheckbox && (
          <div className="pt-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelect}
              aria-label={`Select invitation for ${invitation?.inviteeName}`}
            />
          </div>
        )}

        {/* Participant Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="User" size={18} className="text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {invitation?.inviteeName || 'Unknown Participant'}
                </p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {invitation?.inviteeEmail && (
                    <span className="truncate">{invitation?.inviteeEmail}</span>
                  )}
                  {invitation?.inviteePhone && (
                    <span className="truncate">{invitation?.inviteePhone}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full ${getStatusBgColor()}`}>
              <Icon name={getStatusIcon()} size={14} className={getStatusColor()} />
              <span className={`text-xs font-medium capitalize ${getStatusColor()}`}>
                {status}
              </span>
            </div>
          </div>

          {/* Meeting Details */}
          <div className="mb-3 p-3 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="MapPin" size={14} className="text-muted-foreground flex-shrink-0" />
              <span className="text-xs font-medium text-foreground truncate">
                {invitation?.meetingDetails?.locationName || 'Meeting Location'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Calendar" size={12} />
                <span>{invitation?.meetingDetails?.meetingDate || 'Date TBD'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={12} />
                <span>{invitation?.meetingDetails?.meetingTime || 'Time TBD'}</span>
              </div>
            </div>
          </div>

          {/* Invitation Timeline */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Invitation Sent:</span>
              <span className="text-foreground font-medium">{timeSince}</span>
            </div>
            {status === 'accepted' && invitation?.acceptedAt && (
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Accepted On:</span>
                <span className="text-success font-medium">{formatDate(invitation?.acceptedAt)}</span>
              </div>
            )}
            {status === 'pending' && (
              <div className="flex items-center gap-1 text-xs text-warning">
                <Icon name="AlertCircle" size={12} />
                <span>Awaiting response</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {(status === 'pending' || status === 'declined') && (
              <Button
                variant="outline"
                size="sm"
                iconName="Send"
                iconPosition="left"
                onClick={onResend}
                loading={isResending}
                disabled={isResending}
              >
                {status === 'declined' ? 'Send Follow-up' : 'Resend Invitation'}
              </Button>
            )}
            {status === 'accepted' && (
              <div className="flex items-center gap-2 text-xs text-success">
                <Icon name="CheckCircle" size={14} />
                <span className="font-medium">Confirmed attendance</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationCard;